/**
 * Assets → Drivers: what each driver is qualified for (largest vehicle class,
 * ADR certificate and expiry). Values entered here are Asserted
 * (DriverAttributesDeclared). Suggestions from job history (Inferred) show in
 * empty cells in purple and are saved only when a person fills them in and
 * saves. Source names that matched no single driver are listed underneath for
 * a person to link; nothing is matched silently.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { API_ENDPOINTS } from '../../config/api';
import { authenticatedFetch } from '../../utils/authenticatedFetch';
import { getCurrentUserEmail, readSessionUserEmail } from '../../auth/cognitoAuth';
import { UNKNOWN_CELL } from '../../utils/uiText';
import { suggestionTitle, yesNoLabel, YES_NO_OPTIONS } from '../../utils/assetCapabilities';
import {
  adrExpiryState,
  driverChanges,
  driverSuggestionFor,
  fillDriverSuggestions,
  qualificationsFromRow,
  weightClassOptions,
  type DriverQualifications,
  type DriverRosterApiRow,
  type DriverSuggestableField,
  type UnmatchedDriverNameApi,
} from '../../utils/driverCapabilities';

interface RosterResponse {
  drivers: DriverRosterApiRow[];
  driver_groups_declared: boolean;
  unmatched: { evidence_source: string; window_from: string; window_to: string; names: UnmatchedDriverNameApi[] } | null;
}

const INPUT = 'h-8 px-2 text-sm bg-gray-700 text-white rounded border-2 border-gray-600 focus:outline-none';

async function currentActor(): Promise<string> {
  return readSessionUserEmail() || (await getCurrentUserEmail())?.trim() || 'user';
}

export default function AssetsDrivers({ rosterWeightClasses }: { rosterWeightClasses: string[] }) {
  const [data, setData] = useState<RosterResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editMode, setEditMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const [filter, setFilter] = useState('');
  const [baseline, setBaseline] = useState<Map<string, DriverQualifications>>(new Map());
  const [values, setValues] = useState<Map<string, DriverQualifications>>(new Map());
  const [linkChoice, setLinkChoice] = useState<Record<string, string>>({});
  const [showUnmatched, setShowUnmatched] = useState(false);

  const load = useCallback(async (fresh = false) => {
    setLoading(true);
    setError(null);
    try {
      const res = await authenticatedFetch(API_ENDPOINTS.fleetIntelligence.driversRoster({ fresh }));
      const json = (await res.json().catch(() => ({}))) as RosterResponse & { error?: string };
      if (!res.ok) throw new Error(json.error || `Failed to load drivers (${res.status})`);
      setData(json);
      const v = new Map(json.drivers.map((d) => [d.entityId, qualificationsFromRow(d)]));
      setBaseline(v);
      setValues(new Map(v));
      setEditMode(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load drivers');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const drivers = useMemo(() => data?.drivers ?? [], [data]);
  const shown = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return q ? drivers.filter((d) => d.name.toLowerCase().includes(q)) : drivers;
  }, [drivers, filter]);
  const classOptions = useMemo(
    () =>
      weightClassOptions(rosterWeightClasses, [
        ...[...values.values()].map((v) => v.largest_vehicle_t),
        ...drivers.map((d) => driverSuggestionFor(d.inferred, 'largest_vehicle_t')?.value ?? ''),
      ]),
    [rosterWeightClasses, values, drivers],
  );
  const changes = useMemo(() => driverChanges(baseline, values), [baseline, values]);
  const canFill = useMemo(
    () => fillDriverSuggestions(values, drivers, shown.map((d) => d.entityId)).filled > 0,
    [values, drivers, shown],
  );

  const setField = (id: string, field: keyof DriverQualifications, value: string) => {
    setValues((prev) => {
      const next = new Map(prev);
      const cur = next.get(id);
      if (cur) next.set(id, { ...cur, [field]: value });
      return next;
    });
  };

  const save = async () => {
    if (changes.length === 0) {
      setEditMode(false);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const byId = new Map(drivers.map((d) => [d.entityId, d]));
      const updates = changes.map((c) => ({
        entity_id: c.entityId,
        driver_name: byId.get(c.entityId)?.name ?? '',
        changed_fields: c.changed,
        ...c.values,
      }));
      const res = await authenticatedFetch(API_ENDPOINTS.fleetIntelligence.driversAttributes(), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates, updated_by: await currentActor() }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok || json.success !== true) throw new Error(json.error || json.message || `Failed to save (${res.status})`);
      await load(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save drivers');
    } finally {
      setSaving(false);
    }
  };

  const link = async (name: string) => {
    const entityId = linkChoice[name];
    if (!entityId) return;
    setError(null);
    try {
      const res = await authenticatedFetch(API_ENDPOINTS.fleetIntelligence.driversNameLinks(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ source_driver_name: name, entity_id: entityId, updated_by: await currentActor() }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || `Failed to link (${res.status})`);
      await load(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to link name');
    }
  };

  const renderSuggestable = (d: DriverRosterApiRow, field: DriverSuggestableField) => {
    const v = values.get(d.entityId)?.[field] ?? '';
    const suggestion = v ? undefined : driverSuggestionFor(d.inferred, field);
    const label = (x: string) => (field === 'largest_vehicle_t' ? `${x} T` : yesNoLabel(x));
    if (editMode) {
      return (
        <select
          value={v}
          onChange={(e) => setField(d.entityId, field, e.target.value)}
          className={`${INPUT} ${v ? 'border-sky-500/70' : ''}`}
          aria-label={`${field === 'largest_vehicle_t' ? 'Largest vehicle' : 'ADR certified'} for ${d.name}`}
          title={suggestion ? suggestionTitle(field === 'largest_vehicle_t' ? 'typical_tonnes' : 'adr_equipped', suggestion) : undefined}
        >
          <option value="">Not set</option>
          {(field === 'largest_vehicle_t' ? classOptions.map((c) => ({ value: c, label: `${c} T` })) : YES_NO_OPTIONS).map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
              {suggestion?.value === o.value ? ' (suggested)' : ''}
            </option>
          ))}
        </select>
      );
    }
    if (v) return <span className="text-sky-300 font-semibold" title="Asserted: entered here">{label(v)}</span>;
    if (suggestion?.value) {
      return (
        <span className="text-purple-300 italic" title={suggestionTitle(field === 'largest_vehicle_t' ? 'typical_tonnes' : 'adr_equipped', suggestion)}>
          {label(suggestion.value)}?
        </span>
      );
    }
    return <span className="text-gray-500" title={suggestion ? `Inferred: no suggestion. ${suggestion.evidence}` : 'Unknown: not set'}>{UNKNOWN_CELL}</span>;
  };

  const unmatched = data?.unmatched?.names ?? [];

  return (
    <div className="bg-gray-800/50 rounded-lg p-6 border border-gray-700">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between mb-3">
        <div>
          <h3 className="text-lg font-semibold text-white flex items-center gap-2">
            Drivers
            <span className="inline-flex items-center justify-center min-w-7 px-2 py-0.5 rounded-md text-sm font-medium bg-gray-700 text-gray-200 border border-gray-600">
              {drivers.length}
            </span>
          </h3>
          <p className="text-xs text-gray-400 mt-1 max-w-2xl">
            What each driver is qualified for. Largest vehicle is the heaviest weight class they may drive (the classes in
            Payload (T) above); they may also drive anything lighter.
            {data && !data.driver_groups_declared ? ' Driver groups are not set, so everyone active in the source roster is listed.' : ''}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <input
            type="text"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Filter by name"
            className="bg-gray-900 border border-gray-600 rounded-md px-3 py-1.5 text-sm text-gray-100"
            aria-label="Filter drivers by name"
          />
          {editMode ? (
            <>
              <button
                type="button"
                onClick={() => setValues(fillDriverSuggestions(values, drivers, shown.map((d) => d.entityId)).values)}
                disabled={!canFill || saving}
                className="px-2.5 py-1 text-xs font-medium bg-purple-700 hover:bg-purple-600 disabled:opacity-50 text-white rounded"
                title="Copy suggested values into the empty cells of the drivers shown. Check them, then Save."
              >
                Fill in suggestions
              </button>
              <button
                type="button"
                onClick={() => void save()}
                disabled={saving}
                className="px-2.5 py-1 text-xs font-medium bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white rounded"
              >
                {saving ? 'Saving…' : `Save${changes.length ? ` (${changes.length})` : ''}`}
              </button>
              <button
                type="button"
                onClick={() => {
                  setValues(new Map(baseline));
                  setEditMode(false);
                }}
                disabled={saving}
                className="px-2.5 py-1 text-xs font-medium bg-gray-600 hover:bg-gray-500 text-white rounded"
              >
                Cancel
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                onClick={() => setEditMode(true)}
                disabled={loading || drivers.length === 0}
                className="px-2.5 py-1 text-xs font-medium bg-amber-600 hover:bg-amber-700 disabled:opacity-50 text-white rounded"
              >
                Edit
              </button>
              <button
                type="button"
                onClick={() => void load(true)}
                disabled={loading}
                className="px-2.5 py-1 text-xs font-medium bg-gray-700 hover:bg-gray-600 text-gray-200 rounded"
              >
                {loading ? 'Loading…' : 'Refresh'}
              </button>
            </>
          )}
        </div>
      </div>

      {error && <p className="text-red-400 text-sm mb-2" role="alert">{error}</p>}

      <div className="overflow-x-auto max-h-112 overflow-y-auto border border-gray-700 rounded">
        <table className="w-full text-sm">
          <thead className="bg-gray-800 sticky top-0">
            <tr className="text-left text-gray-400">
              <th className="py-2 px-4">Driver</th>
              <th className="py-2 px-4" title="The source roster's employee group">Group</th>
              <th className="py-2 px-4" title="Heaviest weight class the driver may drive">Largest vehicle</th>
              <th className="py-2 px-4" title="Holds an ADR (dangerous goods) driver training certificate">ADR certified</th>
              <th className="py-2 px-4" title="ADR certificate expiry date">ADR expiry</th>
            </tr>
          </thead>
          <tbody>
            {shown.map((d) => {
              const v = values.get(d.entityId);
              const expiryState = adrExpiryState(v?.adr_expiry ?? '');
              return (
                <tr key={d.entityId} className="border-b border-gray-700/50 hover:bg-gray-700/30">
                  <td className="py-2 px-4 text-white">{d.name}</td>
                  <td className="py-2 px-4 text-gray-400">{d.group || UNKNOWN_CELL}</td>
                  <td className="py-2 px-4">{renderSuggestable(d, 'largest_vehicle_t')}</td>
                  <td className="py-2 px-4">{renderSuggestable(d, 'adr_certified')}</td>
                  <td className="py-2 px-4">
                    {editMode ? (
                      <input
                        type="date"
                        value={v?.adr_expiry ?? ''}
                        onChange={(e) => setField(d.entityId, 'adr_expiry', e.target.value)}
                        className={INPUT}
                        aria-label={`ADR expiry for ${d.name}`}
                      />
                    ) : v?.adr_expiry ? (
                      <span className="inline-flex items-center gap-2">
                        <span className="text-sky-300 font-semibold">{v.adr_expiry}</span>
                        {expiryState === 'expired' && <span className="text-xs uppercase text-red-400">Expired</span>}
                        {expiryState === 'soon' && <span className="text-xs uppercase text-amber-400">Expires soon</span>}
                      </span>
                    ) : (
                      <span className="text-gray-500">{UNKNOWN_CELL}</span>
                    )}
                  </td>
                </tr>
              );
            })}
            {!loading && shown.length === 0 && (
              <tr>
                <td colSpan={5} className="py-4 px-4 text-gray-500">No drivers to show.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {unmatched.length > 0 && (
        <div className="mt-4">
          <button
            type="button"
            onClick={() => setShowUnmatched((s) => !s)}
            className="text-sm text-amber-300 hover:text-amber-200"
            aria-expanded={showUnmatched}
          >
            {showUnmatched ? 'Hide' : 'Show'} {unmatched.length} driver names from {data?.unmatched?.evidence_source} job history that match no single driver
          </button>
          {showUnmatched && (
            <div className="mt-2 space-y-2">
              <p className="text-xs text-gray-400">
                Pick the driver each name belongs to and click Link. Their suggestions then appear on that driver, and
                the name is matched automatically from now on.
              </p>
              {unmatched.map((u) => {
                const top = u.suggestions.largest_vehicle_t?.value;
                return (
                  <div key={u.source_driver_name} className="flex flex-wrap items-center gap-2 text-sm bg-gray-900/50 rounded px-3 py-2">
                    <span className="text-white min-w-40">{u.source_driver_name}</span>
                    <span className="text-gray-400 text-xs">
                      {u.counts.work_days} days{top ? `, drove up to ${top} T` : ''}
                      {u.reason === 'several' ? ', several drivers have this name' : ''}
                    </span>
                    <select
                      value={linkChoice[u.source_driver_name] ?? ''}
                      onChange={(e) => setLinkChoice((p) => ({ ...p, [u.source_driver_name]: e.target.value }))}
                      className={INPUT}
                      aria-label={`Driver for ${u.source_driver_name}`}
                    >
                      <option value="">Choose a driver</option>
                      {u.candidates.length > 0 && (
                        <optgroup label="Closest names">
                          {u.candidates.map((c) => (
                            <option key={c.entityId} value={c.entityId}>{c.name}</option>
                          ))}
                        </optgroup>
                      )}
                      <optgroup label="All drivers">
                        {drivers.map((d) => (
                          <option key={d.entityId} value={d.entityId}>{d.name}</option>
                        ))}
                      </optgroup>
                    </select>
                    <button
                      type="button"
                      onClick={() => void link(u.source_driver_name)}
                      disabled={!linkChoice[u.source_driver_name]}
                      className="px-2.5 py-1 text-xs font-medium bg-green-700 hover:bg-green-600 disabled:opacity-50 text-white rounded"
                    >
                      Link
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
